import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Play } from "lucide-react";
import {
  BRAND, NAV, MARQUEE, HERO, WORKS, ABOUT, VISIT,
} from "./spiralRoastData";
import "./SpiralRoastSite.css";

function Spiral({ className = "" }) {
  return (
    <div className={`sr-spiral ${className}`.trim()} aria-hidden="true">
      <svg viewBox="0 0 200 200" fill="none">
        <path
          d="M100 100 m-2 0 a2 2 0 1 0 4 0 a2 2 0 1 0 -4 0
             m-6 0 a6 6 0 1 0 12 0 a6 6 0 1 0 -12 0
             m-12 0 a12 12 0 1 0 24 0 a12 12 0 1 0 -24 0
             m-20 0 a20 20 0 1 0 40 0 a20 20 0 1 0 -40 0
             m-30 0 a30 30 0 1 0 60 0 a30 30 0 1 0 -60 0
             m-42 0 a42 42 0 1 0 84 0 a42 42 0 1 0 -84 0
             m-56 0 a56 56 0 1 0 112 0 a56 56 0 1 0 -112 0"
          stroke="currentColor"
          strokeWidth="0.75"
          opacity="0.7"
        />
        <circle cx="100" cy="100" r="3" fill="currentColor" opacity="0.9" />
      </svg>
    </div>
  );
}

function Marquee() {
  const text = `${MARQUEE} • ${MARQUEE} • `;
  return (
    <div className="sr-marquee" aria-hidden="true">
      <div className="sr-marquee__track">{text}{text}</div>
    </div>
  );
}

export default function SpiralRoastSite({ embed = false }) {
  const [entered, setEntered] = useState(embed);
  const [withMotion, setWithMotion] = useState(false);
  const [drawer, setDrawer] = useState(false);
  const [reelPlaying, setReelPlaying] = useState(false);
  const [modal, setModal] = useState(null);

  useEffect(() => {
    if (!entered || embed) return undefined;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [entered, embed]);

  const scroll = (id) => {
    setDrawer(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const enter = (motionOn) => {
    setWithMotion(motionOn);
    setEntered(true);
  };

  return (
    <div className={`sr-root${embed ? " sr-root--embed" : ""}${withMotion ? " sr-root--sound" : ""}`}>
      <AnimatePresence>
        {!entered && (
          <motion.div
            className="sr-intro"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="sr-intro__inner">
              <div className="sr-intro__spiral">
                <Spiral />
              </div>
              <div>
                <p className="sr-intro__title">{BRAND.nameEn.toLowerCase()}</p>
                <p className="sr-intro__sub">{BRAND.tagline}</p>
              </div>
              <div className="sr-intro__actions">
                <button type="button" className="sr-intro__btn sr-intro__btn--primary" onClick={() => enter(true)}>
                  모션과 함께 입장
                </button>
                <button type="button" className="sr-intro__btn" onClick={() => enter(false)}>
                  조용히 입장
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {entered && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}>
          <header className="sr-header">
            <button type="button" className="sr-header__brand" onClick={() => scroll("top")}>
              <span className="sr-header__icon"><Spiral /></span>
              <span>{BRAND.nameEn.toLowerCase()}</span>
            </button>
            <nav className="sr-header__nav" aria-label="main">
              {NAV.map((n) => (
                <button key={n.id} type="button" onClick={() => scroll(n.id)}>{n.label}</button>
              ))}
            </nav>
            <button type="button" className="sr-header__menu" aria-label="menu" onClick={() => setDrawer(true)}>
              <Menu size={22} />
            </button>
          </header>

          <AnimatePresence>
            {drawer && (
              <motion.div
                className="sr-drawer"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <button type="button" className="sr-drawer__close" aria-label="close" onClick={() => setDrawer(false)}>
                  <X size={24} />
                </button>
                {NAV.map((n) => (
                  <button key={n.id} type="button" onClick={() => scroll(n.id)}>{n.label}</button>
                ))}
                <a href={`mailto:${BRAND.email}`} style={{ color: "inherit", textDecoration: "none", fontSize: "1rem" }}>
                  {BRAND.email}
                </a>
              </motion.div>
            )}
          </AnimatePresence>

          <section id="top" className="sr-hero">
            <div className="sr-hero__main">
              <button
                type="button"
                className="sr-hero__visual"
                onClick={() => setReelPlaying((p) => !p)}
                aria-label="roast reel 재생"
              >
                <div
                  className="sr-hero__visual-inner"
                  style={{
                    position: "absolute",
                    inset: 0,
                    background: reelPlaying
                      ? "linear-gradient(160deg,#2a1f14 0%,#6b4a2e 35%,#c9a66b 70%,#3d2e22 100%)"
                      : "linear-gradient(160deg,#1a1410 0%,#3d2e22 40%,#8b6914 100%)",
                    transition: "background 1.2s ease",
                    animation: reelPlaying ? "sr-reel-shift 4s ease infinite" : "none",
                  }}
                />
                <div className="sr-hero__play">
                  <div className="sr-hero__play-ring">
                    <Play size={22} fill="currentColor" style={{ marginLeft: 3 }} />
                  </div>
                </div>
              </button>

              <div className="sr-hero__meta">
                <div className="sr-hero__spiral-wrap"><Spiral /></div>
                <p className="sr-hero__eyebrow">
                  {HERO.label} • {HERO.year}
                </p>
                <h1 className="sr-hero__title">{HERO.title}</h1>
                <p className="sr-hero__sub">{HERO.subtitle}</p>
              </div>
            </div>
            <Marquee />
          </section>

          <section id="works" className="sr-section">
            <p className="sr-section__label">menu — origins</p>
            <div className="sr-works">
              {WORKS.map((w, i) => (
                <motion.button
                  key={w.id}
                  type="button"
                  className="sr-work"
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ delay: i * 0.06, duration: 0.5 }}
                  onClick={() => setModal(`${w.title} — ${w.tag}`)}
                >
                  <div className="sr-work__bg" style={{ background: w.tone }} />
                  <div className="sr-work__info">
                    <p className="sr-work__title">{w.title}</p>
                    <p className="sr-work__tag">{w.tag}</p>
                  </div>
                </motion.button>
              ))}
            </div>
          </section>

          <section id="about" className="sr-section">
            <p className="sr-section__label">story</p>
            <div className="sr-about">
              <div>
                <p className="sr-about__lead">{ABOUT.lead}</p>
                <div className="sr-about__body">
                  {ABOUT.body.map((p) => <p key={p.slice(0, 20)}>{p}</p>)}
                </div>
                <div className="sr-about__stats">
                  {ABOUT.stats.map((s) => (
                    <div key={s.label} className="sr-stat">
                      <div className="sr-stat__n">{s.n}</div>
                      <div className="sr-stat__label">{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="sr-about__spiral">
                <Spiral />
              </div>
            </div>
          </section>

          <section id="visit" className="sr-section sr-visit">
            <div>
              <p className="sr-section__label">visit</p>
              <a className="sr-visit__email" href={`mailto:${BRAND.email}`}>{BRAND.email}</a>
              <p className="sr-visit__detail" style={{ marginTop: "1.5rem" }}>{VISIT.note}</p>
            </div>
            <div className="sr-visit__detail">
              <strong>{BRAND.address}</strong>
              <p>{BRAND.phone}</p>
              <p>{BRAND.hours}</p>
              <p style={{ marginTop: "1rem" }}>{VISIT.transit}</p>
            </div>
          </section>

          <footer className="sr-footer">
            <span>© 2026 {BRAND.nameEn.toLowerCase()}</span>
            <span>inspired by pacôme pertant · portfolio demo</span>
          </footer>
        </motion.div>
      )}

      <style>{`
        @keyframes sr-reel-shift {
          0%, 100% { filter: hue-rotate(0deg) brightness(1); }
          50% { filter: hue-rotate(12deg) brightness(1.08); }
        }
      `}</style>

      {modal && (
        <div className="sr-modal-overlay" role="dialog" onClick={() => setModal(null)}>
          <div className="sr-modal" onClick={(e) => e.stopPropagation()}>
            <h3>today&apos;s pick</h3>
            <p>{modal}</p>
            <button type="button" onClick={() => setModal(null)}>닫기</button>
          </div>
        </div>
      )}
    </div>
  );
}
